'use client';

import React, { useState } from 'react';
import { Eye, Edit, Download, Trash2, CheckCircle, MoreHorizontal, Clock, AlertTriangle, FileText, X, Share2, Copy } from 'lucide-react';
import {
  Box,
  Checkbox,
  Chip,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import type { ChipProps } from '@mui/material';
import type { Invoice } from '@crm-src/lib/store/invoicesSlice';
import { formatCurrency } from '@crm-src/lib/utils';
import { ResellerOnly } from '@crm-src/components/reseller';
import { DocumentResellerCell } from '@crm-src/components/document-reseller-cell';
import { formatDate } from '@crm-src/lib/date-utils';
import { SortableHeader } from '@crm-src/components/table/SortableHeader';
import { TableEmptyState } from '@crm-src/components/table/TableEmptyState';
import type { TableColumn } from '@crm-src/components/table/configs';
import type { SortDir } from '@crm-src/components/table/table-query';

interface DocumentListTableProps {
  documents: Invoice[];
  isLoading: boolean;
  documentType: 'invoice' | 'quote';
  /**
   * The DATA columns, in render order — `QUOTES_TABLE.columns` in practice.
   * Passed in rather than imported so the header row and the body cells below
   * can only drift if someone edits one of them without the other, and so the
   * caller owns which config (and therefore which server keys) this table
   * speaks. See components/table/configs.ts.
   */
  columns: readonly TableColumn[];
  /** Active sort column + direction, from the page's `useTableQuery`. */
  sort: string;
  dir: SortDir;
  onSort: (column: string) => void;
  /** Whether the page's search box currently holds a term. */
  hasSearch: boolean;
  /** `useTableQuery().activeFilterCount` — selected VALUES, not facet count. */
  activeFilterCount: number;
  /** Clears every facet — the way out of an over-narrowed list. */
  onClearFilters: () => void;
  selectedDocuments: Set<string>;
  onSelectAll: (checked: boolean) => void;
  onSelectDocument: (documentId: string, checked: boolean) => void;
  onViewDetails: (document: Invoice) => void;
  onEdit: (document: Invoice) => void;
  onDuplicate?: (document: Invoice) => void;
  onDownload: (document: Invoice) => void;
  onDelete: (document: Invoice) => void;
  onShare?: (document: Invoice) => void;
  onMarkAsPaid?: (document: Invoice) => void;
  onConvertToInvoice?: (document: Invoice) => void;
}

// Per-row actions menu anchor (keyed by document id) — replaces the shadcn
// DropdownMenu during the MUI 5 migration.
type RowMenuAnchor = { el: HTMLElement; id: string } | null;

export function DocumentListTable({
  documents,
  isLoading,
  documentType,
  columns,
  sort,
  dir,
  onSort,
  hasSearch,
  activeFilterCount,
  onClearFilters,
  selectedDocuments,
  onSelectAll,
  onSelectDocument,
  onViewDetails,
  onEdit,
  onDuplicate,
  onDownload,
  onDelete,
  onShare,
  onMarkAsPaid,
  onConvertToInvoice
}: DocumentListTableProps) {
  const [menuAnchor, setMenuAnchor] = useState<RowMenuAnchor>(null);

  const isAllSelected = selectedDocuments.size > 0 && selectedDocuments.size === documents.length;
  const isIndeterminate = selectedDocuments.size > 0 && selectedDocuments.size < documents.length;

  // The DATA header cells, rendered identically in all three states (loading,
  // empty, populated) so the header row does not shift as rows arrive — and so
  // an operator can re-sort a slow list without waiting for it.
  //
  // Every cell here sorts on the SERVER key its own body cell renders:
  //   invoice_number → the number Typography    customer   → the company line
  //   total_amount   → the formatted amount     status     → the quote chip
  //   due_date / paid_date / created_at → their formatted dates
  // The checkbox and Actions cells either side stay plain <TableCell>s: neither
  // maps to a server column, so neither can sort.
  const dataHeaders = columns.map((col) => (
    <SortableHeader
      key={col.key}
      column={col.key}
      label={col.label}
      sort={sort}
      dir={dir}
      onSort={onSort}
      {...(col.align ? { align: col.align } : {})}
    />
  ));

  // Helper function to determine if a document is a quote based on document_type or invoice number
  const isQuote = (document: any) => {
    const docType = (document as any)?.document_type || 'invoice';
    if (docType === 'quote') return true;

    // Fallback: check invoice number pattern (quotes start at 500)
    if (document?.invoice_number) {
      const parts = document.invoice_number.split('-');
      if (parts.length === 3) {
        const lastPart = parseInt(parts[2]);
        return lastPart >= 500;
      }
    }
    return false;
  };

  const getStatusBadge = (status: string, document?: any) => {
    // For quotes, show the actual quote status
    if (isQuote(document)) {
      // `effective_status` (2026-09-12): a quote the reseller requested on
      // XOP Anytime derives `expired` from its validity date server-side.
      const quoteStatus = document.effective_status || document.status || 'pending';
      const quoteStatusConfig = {
        pending: { color: 'warning' as ChipProps['color'], icon: Clock, label: 'Pending' },
        accepted: { color: 'success' as ChipProps['color'], icon: CheckCircle, label: 'Accepted' },
        expired: { color: 'error' as ChipProps['color'], icon: AlertTriangle, label: 'Expired' },
        converted: { color: 'success' as ChipProps['color'], icon: CheckCircle, label: 'Converted' },
        rejected: { color: 'default' as ChipProps['color'], icon: X, label: 'Rejected' }
      };

      const config = quoteStatusConfig[quoteStatus as keyof typeof quoteStatusConfig] || quoteStatusConfig.pending;
      const Icon = config.icon;

      return (
        <Chip
          size="small"
          color={config.color ?? "default"}
          icon={<Icon size={12} />}
          label={config.label}
        />
      );
    }

    const statusConfig = {
      draft: { color: 'default' as ChipProps['color'], icon: Edit },
      sent: { color: 'primary' as ChipProps['color'], icon: Eye },
      viewed: { color: 'secondary' as ChipProps['color'], icon: Eye },
      partial_payment: { color: 'warning' as ChipProps['color'], icon: Clock },
      paid: { color: 'success' as ChipProps['color'], icon: CheckCircle },
      unpaid: { color: 'error' as ChipProps['color'], icon: Clock },
      overdue: { color: 'error' as ChipProps['color'], icon: AlertTriangle },
      cancelled: { color: 'default' as ChipProps['color'], icon: AlertTriangle }
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.unpaid;
    const Icon = config.icon;

    // If document is overdue, show both unpaid and overdue badges
    if (document?.overdue && status === 'unpaid') {
      return (
        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
          <Chip size="small" color="error" icon={<Clock size={12} />} label="unpaid" />
          <Chip
            size="small"
            color="error"
            icon={<AlertTriangle size={12} />}
            label="OVERDUE"
            sx={{ fontWeight: 600, animation: "pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite", "@keyframes pulse": { "0%,100%": { opacity: 1 }, "50%": { opacity: 0.4 } } }}
          />
        </Box>
      );
    }

    return (
      <Chip
        size="small"
        color={config.color ?? "default"}
        icon={<Icon size={12} />}
        label={status.replace('_', ' ')}
      />
    );
  };

  if (isLoading) {
    return (
      <Table>
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: 48 }}>
              <Skeleton variant="rounded" width={16} height={16} />
            </TableCell>
            {dataHeaders}
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {[...Array(5)].map((_, i) => (
            <TableRow key={i}>
              {[...Array(10)].map((_, j) => (
                <TableCell key={j}>
                  {j === 0 ? (
                    <Skeleton variant="rounded" width={16} height={16} />
                  ) : (
                    <Skeleton variant="rounded" width={80} height={16} />
                  )}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  }

  if (!documents || documents.length === 0) {
    return (
      <Table>
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: 48 }}>
              <Checkbox
                checked={false}
                disabled
                size="small"
                inputProps={{ 'aria-label': 'Select all' }}
              />
            </TableCell>
            {dataHeaders}
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
              {/* Names whichever narrowing actually emptied the list. The old
                  copy said "Create your first quote to get started" even when
                  the tenant had hundreds and one facet was on. */}
              <TableEmptyState
                entityNoun={`${documentType}s`}
                hasSearch={hasSearch}
                activeFilterCount={activeFilterCount}
                onClearFilters={onClearFilters}
                emptyTitle={`No ${documentType}s found`}
                emptyBody={`Create your first ${documentType} to get started`}
              />
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    );
  }

  return (
    <Table>
      <TableHead>
        <TableRow>
          <TableCell sx={{ width: 48 }}>
            <Checkbox
              checked={isAllSelected}
              indeterminate={isIndeterminate}
              onChange={(e) => onSelectAll(e.target.checked)}
              size="small"
              inputProps={{ 'aria-label': `Select all ${documentType}s` }}
            />
          </TableCell>
          {dataHeaders}
          <TableCell>Actions</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {documents.filter(doc => doc && doc.id).map((document) => (
          <TableRow
            key={document.id}
            hover
          >
            <TableCell sx={{ width: 48 }} onClick={(e) => e.stopPropagation()}>
              <Checkbox
                checked={selectedDocuments.has(document.id.toString())}
                onChange={(e) =>
                  onSelectDocument(document.id.toString(), e.target.checked)
                }
                size="small"
                inputProps={{ 'aria-label': `Select ${documentType} ${document.invoice_number}` }}
              />
            </TableCell>
            <TableCell
              sx={{ fontWeight: 500, cursor: "pointer" }}
              onClick={() => onViewDetails(document)}
            >
              <Typography sx={{ fontSize: 14 }}>{document.invoice_number || 'N/A'}</Typography>
            </TableCell>
            <TableCell
              sx={{ cursor: "pointer", maxWidth: 200 }}
              onClick={() => onViewDetails(document)}
            >
              {/* CUSTOMER = the end customer (2026-09-15). For a reseller's
                  portal request the row's own customer_company_name is the
                  RESELLER (the bill-to, which the PDF needs); the customer the
                  request is FOR rides on reseller_quote. The reseller itself has
                  its own column now. */}
              <Box sx={{ maxWidth: 200 }} data-cy="document-customer">
                {(() => {
                  const plain = (
                    <>
                      <Typography sx={{ fontSize: 14, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={document.customer_company_name || 'N/A'}>{document.customer_company_name || 'N/A'}</Typography>
                      <Typography sx={{ fontSize: 14, color: "text.secondary", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={document.customer_name || 'N/A'}>{document.customer_name || 'N/A'}</Typography>
                    </>
                  )
                  if (!(document.locked && document.reseller_quote)) return plain
                  // Reseller-derived (the request's customer + who asked): crm-api
                  // strips `reseller_quote` for a walled viewer, and this is the
                  // SPA's own gate on the same data — a walled viewer gets the
                  // plain lines.
                  return (
                    <ResellerOnly fallback={plain}>
                      <Typography sx={{ fontSize: 14, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={document.reseller_quote.customer_name || '—'}>{document.reseller_quote.customer_name || '—'}</Typography>
                      <Typography sx={{ fontSize: 12, color: "text.secondary", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={document.reseller_quote.requested_by_email || ''}>Requested by {document.reseller_quote.requested_by_email || '—'}</Typography>
                    </ResellerOnly>
                  )
                })()}
              </Box>
            </TableCell>
            {/* RESELLER: the recipient reseller, in the ONE cell both tables
                share (components/document-reseller-cell.tsx). */}
            <DocumentResellerCell reseller={document.bill_to_reseller} locked={document.locked} onClick={() => onViewDetails(document)} />
            <TableCell
              sx={{ cursor: "pointer" }}
              onClick={() => onViewDetails(document)}
            >
              <Typography sx={{ fontSize: 14, fontWeight: 500 }}>{formatCurrency(document.total_amount || 0)}</Typography>
            </TableCell>
            <TableCell
              sx={{ cursor: "pointer" }}
              onClick={() => onViewDetails(document)}
            >
              {getStatusBadge(document.payment_status || 'unpaid', document)}
            </TableCell>
            <TableCell
              sx={{ cursor: "pointer" }}
              onClick={() => onViewDetails(document)}
            >
              <Typography sx={{ fontSize: 14, ...(document.payment_status === 'overdue' ? { color: "error.main", fontWeight: 500 } : {}) }}>
                {formatDate(document.due_date)}
              </Typography>
            </TableCell>
            <TableCell
              sx={{ cursor: "pointer" }}
              onClick={() => onViewDetails(document)}
            >
              <Typography sx={{ fontSize: 14, color: document.paid_date ? "success.main" : "text.disabled", ...(document.paid_date ? { fontWeight: 500 } : {}) }}>
                {document.paid_date ? formatDate(document.paid_date) : '-'}
              </Typography>
            </TableCell>
            <TableCell
              sx={{ cursor: "pointer" }}
              onClick={() => onViewDetails(document)}
            >
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Typography component="span" sx={{ fontSize: 14 }}>{formatDate(document.created_at)}</Typography>
                {document.created_by && (
                  <Typography component="span" sx={{ fontSize: 12, color: "text.secondary", mt: 0.25 }}>
                    {document.created_by.full_name}
                  </Typography>
                )}
              </Box>
            </TableCell>
            <TableCell onClick={(e) => e.stopPropagation()}>
              <IconButton size="small" sx={{ width: 32, height: 32 }} aria-label="Open menu" onClick={(e) => setMenuAnchor({ el: e.currentTarget, id: document.id.toString() })}>
                <MoreHorizontal size={16} />
              </IconButton>
              <Menu
                anchorEl={menuAnchor?.id === document.id.toString() ? menuAnchor.el : null}
                open={menuAnchor?.id === document.id.toString()}
                onClose={() => setMenuAnchor(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
              >
                <MenuItem onClick={() => { setMenuAnchor(null); onViewDetails(document); }}>
                  <Box sx={{ mr: 1, display: "flex" }}><Eye size={16} /></Box>
                  View Details
                </MenuItem>
                {/* A quote the reseller requested and decided on XOP Anytime is
                    locked against EDITING: the CRM converts it or deletes it
                    (which removes it from the reseller's portal too, 2026-09-15),
                    it never rewrites the terms (crm-api answers 409 QUOTE_LOCKED). */}
                {!document.locked && (
                  <MenuItem onClick={() => { setMenuAnchor(null); onEdit(document); }}>
                    <Box sx={{ mr: 1, display: "flex" }}><Edit size={16} /></Box>
                    Edit {documentType === 'quote' ? 'Quote' : 'Invoice'}
                  </MenuItem>
                )}
                {onDuplicate && (
                  <MenuItem onClick={() => { setMenuAnchor(null); onDuplicate(document); }}>
                    <Box sx={{ mr: 1, display: "flex" }}><Copy size={16} /></Box>
                    Duplicate
                  </MenuItem>
                )}
                {/* Convert to Invoice - only for quotes that aren't expired or converted */}
                {isQuote(document) &&
                 onConvertToInvoice &&
                 document.status !== 'expired' &&
                 document.status !== 'converted' &&
                 document.status !== 'rejected' && (
                  <MenuItem onClick={() => { setMenuAnchor(null); onConvertToInvoice(document); }}>
                    <Box sx={{ mr: 1, display: "flex" }}><FileText size={16} /></Box>
                    Convert to Invoice
                  </MenuItem>
                )}
                {document.payment_status !== 'paid' && !isQuote(document) && onMarkAsPaid && (
                  <MenuItem onClick={() => { setMenuAnchor(null); onMarkAsPaid(document); }}>
                    <Box sx={{ mr: 1, display: "flex" }}><CheckCircle size={16} /></Box>
                    Mark as Paid
                  </MenuItem>
                )}
                <Divider />
                {onShare && (
                  <MenuItem onClick={() => { setMenuAnchor(null); onShare(document); }}>
                    <Box sx={{ mr: 1, display: "flex" }}><Share2 size={16} /></Box>
                    Share
                  </MenuItem>
                )}
                <MenuItem onClick={() => { setMenuAnchor(null); onDownload(document); }}>
                  <Box sx={{ mr: 1, display: "flex" }}><Download size={16} /></Box>
                  Download PDF
                </MenuItem>
                <Divider />
                <MenuItem
                  onClick={() => { setMenuAnchor(null); onDelete(document); }}
                  sx={{ color: "error.main" }}
                >
                  <Box sx={{ mr: 1, display: "flex" }}><Trash2 size={16} /></Box>
                  Delete
                </MenuItem>
              </Menu>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}


